const FALLBACK_OUTPUT_ID = -1;
const UNSEEN_NODES_OUTPUT_ID = -222;

import {CampaignOptimizationData} from 'common/src/modules/optimization/optimization.types';
import {DateTime} from 'luxon';
import {DimensionValue, MetricValue} from '../analytics/analytics.types';

enum CLInsightsType {
  General = 'General',
  SocialGeneral = 'SocialGeneral',
  Video = 'Video',
  Audio = 'Audio',
  Social = 'Social',
  Finance = 'Finance',
  ExternalData = 'External Data',
}

enum ChartOptions {
  Results = 'results',
  Impressions = 'imps',
  ResultRate = 'resultsRate',
}

enum OptimizationRow {
  Node = 'node',
  Impressions = 'imps',
  Results = 'results',
  ResultsRate = 'resultsRate',
  CR = 'cr',
  Status = 'status',
}

enum ActionOptions {
  Settings = 'Settings',
  ExportToExcel = 'ExportToExcel',
  ExportToPDF = 'ExportToPDF',
}

interface RemoteNodeInsightsData extends InsightsMetricsData {
  targetNodeId: number;
}

interface RemoteNodeInsightsDataWithSource extends RemoteNodeInsightsData {
  sourceNodeId: number;
  outputId: number;
}

type InsightsData = Partial<Record<MetricValue, number>>;

interface InsightsMetricsData {
  MetricsData: InsightsData;
}

interface NodeInsightsData {
  value: number;
  percentageOfTotal: number;
}

interface RemoteCampaignInsightsData {
  totals: InsightsMetricsData;
  insights: RemoteNodeInsightsDataWithSource[];
  nodeInsights: RemoteNodeInsightsData[];
}

interface NodeInsights {
  totals: Record<MetricValue, NodeInsightsData>;
  unseen?: Record<MetricValue, NodeInsightsData>;
  outputs?: Record<number, Record<string, NodeInsightsData>>;
}

interface CampaignInsightsData {
  totals: InsightsData;
  nodes: NodesInsightsOutputInputData;
}

interface RemoteExperiment {
  pubId: string;
  plcId: string;
  uiData: string;
  plcName: string;
  startDate: string;
  isActive: boolean;
  archiveDate: string;
  isArchived: boolean;
  variationId: string;
  isComplete: boolean;
  variantIds: string[];
  experimentId: number;
  experimentName: string;
  inActivityReason: string;
  lastActivityDate: string;
  configuration: CampaignOptimizationData;
  totals: DetailedExperimentStagesTotalData;
}

interface Experiment
  extends Omit<RemoteExperiment, 'startDate' | 'archiveDate' | 'lastActivityDate'> {
  startDate: DateTime;
  archiveDate: DateTime;
  lastActivityDate: DateTime;
}

interface DetailedExperimentData {
  isActive: boolean;
  optimizeBy: string;
  isComplete: boolean;
  inActivityReason: string;
  chartData: DetailedChartData;
  tableData: DetailedOptimizationTableData[];
  configuration: CampaignOptimizationData;
  placements: DetailedExperimentPlacement[];
  totals?: DetailedExperimentStagesTotalData;
}

interface OptimizationNode {
  name: string;
  color: string;
  nodeId?: number;
  campaignName?: string;
  connectedNodeName?: string;
}

interface DetailedOptimizationTableData {
  id: string;
  color: string;
  node: OptimizationNode;
  cr: number;
  confidenceLevel: number;
  imps: number;
  name?: string;
  date?: string;
  results: number;
  resultsRate: number;
  cellHeight?: number;
  averageValue?: number;
  resultsTotal?: number;
  impressionsTotal?: number;
  isFullWidthCellRenderer?: boolean;
  status: {
    dropDate: DateTime;
    isWinner: boolean;
    isActive: boolean;
    isCurrentPhase: boolean;
  };
}

interface DetailedChartData {
  colors: string[];
  xAxisLabels: DateTime[];
  series: DetailedChartSeriesData[];
}
interface DetailedChartSeriesData {
  name: string;
  data: {
    imps: number[];
    results: number[];
    resultsRate: number[];
    [key: string]: number[];
  };
}

interface DetailedExperimentPlacement {
  placementId: string;
  publisherId: string;
  placementName: string;
}

interface RemoteDetailedExperiment extends RemoteExperiment {
  stages: RemoteDetailedExperimentStagesData[];
  uiVariantsGraphData: RemoteDetailedExperimentGraphData[];
}

interface DetailedExperiment extends Experiment {
  optimizeBy: string;
  stages: DetailedExperimentStagesData[];
  uiVariantsGraphData: DetailedExperimentGraphData[];
}

interface RemoteDetailedExperimentGraphData {
  variationId: string;
  variationName: string;
  dataPerDate: RemoteDetailedExperimentGraphDataPerDate[];
}

interface DetailedExperimentGraphData {
  variationId: string;
  variationName: string;
  dataPerDate: DetailedExperimentGraphDataPerDate[];
}

interface RemoteDetailedExperimentGraphDataPerDate {
  desc: string;
  imps: number;
  results: number;
  event_date: string;
  resultsRate: number;
}

interface DetailedExperimentGraphDataPerDate
  extends Omit<RemoteDetailedExperimentGraphDataPerDate, 'event_date'> {
  date: DateTime;
}

interface RemoteDetailedExperimentStagesData {
  end: string;
  start: string;
  totals: DetailedExperimentStagesTotalData;
  variantsData: RemoteDetailedExperimentVariantsData[];
}

interface DetailedExperimentStagesData {
  end: DateTime;
  start: DateTime;
  totals: DetailedExperimentStagesTotalData;
  variantsData: DetailedExperimentVariantsData[];
}

interface DetailedExperimentStagesTotalData {
  imps: number;
  results: number;
  resultsRate: number;
}

interface RemoteDetailedExperimentVariantsData {
  imps: number;
  dropDate: string;
  results: number;
  variantId: string;
  daysOfData: number;
  resultsRate: number;
  description: string;
  servingShare: number;
  servingPercent: number;
  shortDescription: string;
  confidenceLevelPercent: number;
  performanceLiftPercent: number;
}
interface DetailedExperimentVariantsData
  extends Omit<RemoteDetailedExperimentVariantsData, 'dropDate'> {
  dropDate: DateTime;
}

interface ExperimentSelectOption extends OptimizationAction {
  experimentId: number;
  plcId: string;
  pubId: string;
  plcName: string;
}
interface OptimizationAction {
  value: string;
  label: string;
}

interface NodesInsightsOutputInputData {
  outgoingData: Record<number, NodeInsights>;
  incomingData: Record<number, NodeInsights>;
}

interface InsightMetricsOption {
  id: number;
  name: string;
  isClinchInsightsMetric: boolean;
  metrics: MetricValue[];
  customConversions: MetricValue[];
}

type InsightsObj = Record<
  MetricValue,
  {value: number; percentageOfTotal: number; proportionalSize: number}
>;

interface RemoteAvailableMetricsForPreset {
  supportedMetrics: MetricValue[];
  supportedCustomConversion: MetricValue[];
}

interface RemoteAvailableFilter {
  category: string;
  value: DimensionValue;
}

type AvailableMetricsForPreset = Set<MetricValue>;

interface MetaCustomConversions {
  id: string;
  name: string;
}

enum InsightsSearchParamKey {
  Date = 'date',
  Metric = 'metric',
  Preset = 'preset',
  Filters = 'filters',
  Timezone = 'timezone',
}

interface RemoteGeneralStatistics {
  ActiveSince: string;
  Metrics: Record<MetricValue, number>;
}

export {
  ActionOptions,
  AvailableMetricsForPreset,
  CLInsightsType,
  CampaignInsightsData,
  ChartOptions,
  DetailedChartData,
  DetailedChartSeriesData,
  DetailedExperiment,
  DetailedExperimentData,
  DetailedExperimentPlacement,
  DetailedExperimentStagesData,
  DetailedExperimentVariantsData,
  DetailedOptimizationTableData,
  Experiment,
  ExperimentSelectOption,
  FALLBACK_OUTPUT_ID,
  InsightMetricsOption,
  InsightsData,
  InsightsMetricsData,
  InsightsObj,
  NodeInsights,
  NodeInsightsData,
  NodesInsightsOutputInputData,
  OptimizationAction,
  RemoteAvailableMetricsForPreset,
  RemoteCampaignInsightsData,
  RemoteDetailedExperiment,
  RemoteDetailedExperimentGraphData,
  RemoteDetailedExperimentStagesData,
  RemoteExperiment,
  RemoteNodeInsightsData,
  RemoteNodeInsightsDataWithSource,
  UNSEEN_NODES_OUTPUT_ID,
  RemoteAvailableFilter,
  MetaCustomConversions,
  InsightsSearchParamKey,
  RemoteGeneralStatistics,
  OptimizationRow,
};
